import { defineStore } from 'pinia'
import axios from 'crypto-fetch/axios'
import { union, uniqWith } from 'lodash-es'
import { useEventBus } from '@vueuse/core'
import { turnMarketCombosToMap } from '@ms/components/match/update-details.js'
import { cancelPending } from '@ms/utils/cancel-pending.js'
import { PENDING_CANCEL } from '@ms/config/message.js'

export const useDetailStore = defineStore('detail', {
  state: () => ({
    eventId: '',
    comboMarket: null,
    comboMarketGroups: {},
    conflictMarkets: [],
    loading: false,
    curConflictMarkets: [],
    conflictTimer: null,
    conflictMarketsTemp: [],
    popularTimer: null,
    changePopular: [],
    isCombosFlag: false
  }),
  actions: {
    async getComboMarkets(eventId) {
      this.updateSub(true)
      // 不同比赛清除历史数据
      if (this.eventId !== eventId) {
        this.comboMarketGroups = {}
      }
      this.eventId = eventId
      const { bizCode, data } = await axios.get(`/facts-center/query/frontend/match/${eventId}/bet-builder/combos`, { self: true })
      if (bizCode === 10000) {
        if (data.comboMarketGroups || data.popularCombos) {
          if (data.comboMarketGroups.length) {
            this.comboMarket = data
          }
          if (data.comboMarketGroups) {
            this.setMarketComboGroups(data.comboMarketGroups)
            this.updateSub()
          }
        }
      }
      return { bizCode }
    },
    async getConflictMarkets(market, isDelete = false, eventId = '', isCombos = false) {
      if (this.isCombosFlag) return false
      if (this.eventId && eventId && this.eventId !== eventId && isDelete) return false

      this.isCombosFlag = isCombos

      this.conflictMarketsTemp = [...this.conflictMarketsTemp, ...market]
      const deleteAll = eventId && this.eventId !== eventId
      const markets = this.conflictMarketsTemp
      const isAllDelete = (deleteAll || this.curConflictMarkets.length === 1 || !markets || !markets.length) && isDelete
      if (!isAllDelete) {
        // 对于重复请求先loading进行数据聚合，最后请求一次
        this.updateLoading(true)
      }

      this.conflictTimer && clearTimeout(this.conflictTimer)

      this.conflictTimer = setTimeout(() => {
        updataConflictMarkets()
        this.conflictMarketsTemp = []
        clearTimeout(this.conflictTimer)
      }, 200)

      const updataConflictMarkets = async () => {
        cancelPending.cancel('conflictingMarkets')

        const markets = this.conflictMarketsTemp
        const isAllDelete = eventId && this.eventId !== eventId

        if (isAllDelete) {
          this.conflictMarkets = []
          this.curConflictMarkets = []
          this.updateLoading(false)
          this.isCombosFlag = false
          return false
        }

        let body
        const _new = markets.map((x) => {
          const { market, outcome } = x
          return {
            marketId: market.id,
            outcomeId: outcome.id,
            specifiers: market.specifiers || ''
          }
        })

        if (isDelete) {
          body = this.curConflictMarkets.filter((x) => {
            return !_new.some((item) => item.marketId === x.marketId && item.outcomeId === x.outcomeId && item.specifiers === x.specifiers)
          })
        } else {
          body = uniqWith([...this.curConflictMarkets, ..._new], (val, oldVal) => {
            const { marketId: mId, outcomeId: oId, specifiers: s } = val
            const { marketId: _mId, outcomeId: _oId, specifiers: _s } = oldVal
            return mId === _mId && oId === _oId && s === _s
          })
        }

        this.curConflictMarkets = body
        if (!body.length) {
          this.conflictMarkets = []
          this.updateLoading(false)
          return false
        }

        try {
          this.updateLoading(true)
          const data = await axios.post(`/facts-center/query/frontend/match/${this.eventId || eventId}/bet-builder/conflicting-markets`, body, {
            ...cancelPending.getCancelToken('conflictingMarkets')
          })
          this.conflictMarkets = data
          this.updateLoading(false)
        } catch (error) {
          if (error.message !== PENDING_CANCEL) {
            this.updateLoading(false)
          }
          console.log('error', error)
          return Promise.resolve()
        }
        this.isCombosFlag = false
      }
    },
    async getOdds(body) {
      const data = await axios.post(`/facts-center/query/frontend/match/${this.eventId}/bet-builder/odds`, body)
      return data
    },
    setMarketComboGroups(data) {
      if (data) {
        const { order } = this.comboMarketGroups
        if (!order || !order.length) {
          this.comboMarketGroups = turnMarketCombosToMap(data)
        } else {
          const newComboMarketGroups = turnMarketCombosToMap(data)
          const { map, order } = this.comboMarketGroups
          const { map: newMap, order: newOrder } = newComboMarketGroups

          const reslutOrder = union(order, newOrder)
          const reslutMap = {}

          reslutOrder.forEach((x) => {
            if (!newMap[x]) {
              reslutMap[x] = map[x]
            } else {
              if (Array.isArray(newMap[x])) {
                reslutMap[x] = uniqWith([...map[x], ...newMap[x]], (val, oldVal) => {
                  const { id, specifiers } = val
                  const { id: _id, specifiers: _specifiers } = oldVal
                  return id === _id && specifiers === _specifiers
                })
              } else {
                reslutMap[x] = newMap[x]
              }
            }
          })

          this.comboMarketGroups = { map: reslutMap, order: reslutOrder }
        }
      }
    },
    clearComboMarket() {
      this.comboMarket = null
      this.conflictMarkets = []
      this.curConflictMarkets = []
    },
    updateStatus(status) {
      const { map, order } = this.comboMarketGroups
      order.forEach((id) => {
        const m = map[id]
        if (Array.isArray(m)) {
          m.forEach((market) => {
            if (market.status === 0 || market.status === 1) {
              market.status = status
            }
          })
        } else {
          if (m.status === 0 || m.status === 1) {
            m.status = status
          }
        }
      })
    },
    updateMarket(market, pushInfo) {
      const { map, order } = this.comboMarketGroups
      const index = order.findIndex((x) => {
        if (Array.isArray(map[x])) {
          return map[x].some((item) => {
            const { specifiers, id } = item
            const isSpecifiers = specifiers ? specifiers === market.specifiers : true
            return id + '' === market.id + '' && isSpecifiers
          })
        } else {
          const { specifiers, id } = map[x]
          const isSpecifiers = specifiers ? specifiers === market.specifiers : true
          return id + '' === market.id + '' && isSpecifiers
        }
      })

      if (index === -1) return false
      const filterMarket = this.comboMarketGroups.map[order[index]]
      if (Array.isArray(filterMarket)) {
        for (let i = 0; i < filterMarket.length; i++) {
          const { specifiers, id } = filterMarket[i]
          const status = specifiers ? specifiers === market.specifiers : true
          if (id + '' === market.id + '' && status) {
            Object.assign(this.comboMarketGroups.map[order[index]][i], pushInfo)
            break
          }
        }
      } else {
        const { specifiers, id } = filterMarket
        const isSpecifiers = specifiers ? specifiers === market.specifiers : true
        if (id + '' === market.id + '' && isSpecifiers) {
          Object.assign(this.comboMarketGroups.map[order[index]], pushInfo)
        }
      }
    },
    updateOutcome(market, pushInfo) {
      this.updatePopular(market, pushInfo)
      const { map, order } = this.comboMarketGroups
      const { market: marketInfo } = market

      const index = order.findIndex((x) => {
        if (Array.isArray(map[x])) {
          return map[x].some((m) => Number(m.id) === Number(marketInfo.id))
        } else {
          return Number(map[x].id) === Number(marketInfo.id)
        }
      })

      if (index === -1) return false

      if (Array.isArray(map[order[index]])) {
        map[order[index]].forEach((m, mIndex) => {
          const { order: _order } = m.outcomes
          if (Number(m.id) === Number(marketInfo.id) && m.specifiers === marketInfo.specifiers) {
            for (let i = 0; i < _order.length; i++) {
              if (_order[i] === pushInfo.id) {
                Object.assign(this.comboMarketGroups.map[order[index]][mIndex].outcomes.map[_order[i]], pushInfo)
                break
              }
            }
          }
        })
      } else {
        const { order: _order } = map[order[index]].outcomes
        for (let i = 0; i < _order.length; i++) {
          if (_order[i] === pushInfo.id) {
            Object.assign(this.comboMarketGroups.map[order[index]].outcomes.map[_order[i]], pushInfo)
            break
          }
        }
      }
    },
    updatePopular(market) {
      if (!this.comboMarket?.popularCombos?.length) return false
      const { popularCombos } = this.comboMarket

      const { id, specifiers, description } = market.market

      this.popularTimer && clearTimeout(this.popularTimer)

      for (let index = 0; index < popularCombos.length; index++) {
        let result = null

        for (let i = 0; i < popularCombos[index].selections.length; i++) {
          const { id: sId, specifiers: sSpecifiers, description: sDescription } = popularCombos[index].selections[i]
          const isSpecifiers = sSpecifiers ? sSpecifiers === specifiers : true

          if (sId === id && isSpecifiers && sDescription === description) {
            if (!this.changePopular.includes(index)) {
              this.changePopular.push(index)
            }
            result = index + 1
            break
          }
        }

        if (result) break
      }

      this.popularTimer = setTimeout(() => {
        this.changePopular.forEach(async (x) => {
          try {
            const body = popularCombos[x].selections.map((selection) => {
              return {
                marketId: selection.id,
                specifiers: selection.specifiers,
                outcomeId: selection.outcomes[0].id
              }
            })
            const data = await this.getOdds(body)
            popularCombos[x].odds = data.odds
            this.changePopular = []
            clearTimeout(this.popularTimer)
          } catch (error) {
            console.log('error', error)
          }
        })
      }, 100)
    },
    updateSub(cancel = false) {
      if (cancel) {
        useEventBus('updateMarket').off(this.updateMarket)
        useEventBus('updateOutcome').off(this.updateOutcome)
        useEventBus('updateMarketStatus').off(this.updateStatus)
      } else {
        useEventBus('updateMarket').on(this.updateMarket)
        useEventBus('updateOutcome').on(this.updateOutcome)
        useEventBus('updateMarketStatus').on(this.updateStatus)
      }
    },
    updateLoading(status) {
      this.loading = status
    },
    deteleConflictMarket() {
      this.conflictMarketsTemp = []
      this.conflictMarkets = []
      this.curConflictMarkets = []
    }
  }
})
